import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import {
  XMarkIcon,
  UserIcon,
  ComputerDesktopIcon,
  ClockIcon,
  TrophyIcon,
  ChatBubbleLeftRightIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

/**
 * Audio Player Component - Fetches audio with authorization
 */
const AudioPlayer = ({ audioId, role }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadAudio = async () => {
      try {
        setLoading(true);
        setError(false);

        // Fetch audio with authorization headers
        const response = await api.get(`/api/audio/recording/${audioId}`, {
          responseType: 'blob',
        });

        // Create blob URL with correct MIME type
        const contentType = response.headers['content-type'] ||
                          (role === 'user' ? 'audio/wav' : 'audio/wav');
        const blob = new Blob([response.data], { type: contentType });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
      } catch (err) {
        console.error('Error loading audio:', err);
        setError(true);
        toast.error('Не удалось загрузить аудио');
      } finally {
        setLoading(false);
      }
    };

    if (audioId) {
      loadAudio();
    }

    // Cleanup blob URL on unmount
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <div className="animate-spin h-3 w-3 border-2 border-gray-400 border-t-transparent rounded-full"></div>
        Загрузка аудио...
      </div>
    );
  }

  if (error || !audioUrl) {
    return (
      <div className="text-xs text-red-500">
        Ошибка загрузки аудио
      </div>
    );
  }

  return (
    <audio
      controls
      className="w-full h-8"
      preload="metadata"
      style={{ maxWidth: '300px' }}
      src={audioUrl}
    >
      Ваш браузер не поддерживает аудио.
    </audio>
  );
};

/**
 * Session Transcript Component
 * Shows detailed conversation history for a session
 */
const SessionTranscript = ({ sessionId, onClose, userRole }) => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    if (sessionId) {
      loadSession();
    }
  }, [sessionId]);

  const loadSession = async () => {
    try {
      setLoading(true);

      // Choose endpoint based on role
      const endpoint = userRole === 'supervisor' || userRole === 'director' || userRole === 'admin'
        ? `/api/supervisor/sessions/${sessionId}`
        : `/api/user/sessions/${sessionId}`;

      const response = await api.get(endpoint);
      setSession(response.data.session);
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Не удалось загрузить диалог');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (ms) => {
    if (!ms) return '—';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}м ${seconds}с`;
  };

  if (!sessionId) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Загрузка диалога...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <p className="text-gray-900 mb-4">Сессия не найдена</p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <button
                  onClick={onClose}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Назад"
                >
                  <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
                </button>
                <h2 className="text-2xl font-bold text-gray-900">
                  {session.scenario_name || 'Сессия'}
                </h2>
              </div>

              {/* Session Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                {session.manager && (
                  <div>
                    <p className="text-xs text-gray-600">Менеджер</p>
                    <p className="text-sm font-medium text-gray-900">
                      {session.manager.firstName} {session.manager.lastName}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-xs text-gray-600">Дата</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(session.created_at).toLocaleDateString('ru-RU')}
                  </p>
                </div>

                {session.score !== null && session.score !== undefined && (
                  <div>
                    <p className="text-xs text-gray-600">Оценка</p>
                    <div className="flex items-center gap-1">
                      <TrophyIcon className="h-4 w-4 text-yellow-600" />
                      <p className="text-sm font-bold text-gray-900">{session.score}</p>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs text-gray-600">Длительность</p>
                  <div className="flex items-center gap-1">
                    <ClockIcon className="h-4 w-4 text-blue-600" />
                    <p className="text-sm font-medium text-gray-900">
                      {formatDuration(session.duration_ms)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="mt-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  session.status === 'completed'
                    ? 'bg-green-100 text-green-800'
                    : session.status === 'in_progress'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {session.status === 'completed' ? 'Завершено' :
                   session.status === 'in_progress' ? 'В процессе' : 'Прервано'}
                </span>
              </div>
            </div>

            <button
              onClick={onClose}
              className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {session.analysis && (
            <div className="max-w-4xl mx-auto mb-6 bg-white border border-purple-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Результат и рекомендации</h3>
                  <p className="text-sm text-gray-500">
                    Выполнено {session.analysis.metrics?.objectivesCompleted ?? 0} из{' '}
                    {session.analysis.metrics?.totalObjectives ?? 0} целей
                  </p>
                </div>
                <div className="text-3xl font-bold text-purple-700">
                  {session.analysis.overallScore}%
                </div>
              </div>

              {session.analysis.strengths?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-green-800 mb-2">Сильные стороны</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    {session.analysis.strengths.map((item, index) => (
                      <li key={`strength-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {session.analysis.improvements?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-amber-800 mb-2">Области для улучшения</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    {session.analysis.improvements.map((item, index) => (
                      <li key={`improvement-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {session.analysis.recommendations?.length > 0 && (
                <div className="mb-4 bg-purple-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">Рекомендации</h4>
                  <ol className="list-decimal pl-5 space-y-1 text-sm text-purple-900">
                    {session.analysis.recommendations.map((item, index) => (
                      <li key={`recommendation-${index}`}>{item}</li>
                    ))}
                  </ol>
                </div>
              )}

              {session.analysis.detailedFeedback && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Подробная обратная связь</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {session.analysis.detailedFeedback}
                  </p>
                </div>
              )}
            </div>
          )}

          {session.status === 'completed' && session.analysis_status === 'failed' && (
            <div className="max-w-4xl mx-auto mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
              Разговор сохранён, но AI-анализ временно не сформирован. Нулевая оценка за эту попытку не учитывается.
            </div>
          )}

          {!session.messages || session.messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <ChatBubbleLeftRightIcon className="h-16 w-16 mb-4 text-gray-400" />
              <p className="text-lg">Нет сообщений в этой сессии</p>
              <p className="text-sm mt-2">Диалог еще не начался или был удален</p>
            </div>
          ) : (
            <div className="space-y-4 max-w-4xl mx-auto">
              {session.messages.map((message, index) => (
                <div
                  key={message.id || index}
                  className={`flex gap-3 ${
                    message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
                  }`}
                >
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.sender === 'user'
                      ? 'bg-blue-600'
                      : 'bg-gray-700'
                  }`}>
                    {message.sender === 'user' ? (
                      <UserIcon className="h-5 w-5 text-white" />
                    ) : (
                      <ComputerDesktopIcon className="h-5 w-5 text-white" />
                    )}
                  </div>

                  {/* Message Bubble */}
                  <div className={`flex flex-col max-w-[70%] ${
                    message.sender === 'user' ? 'items-end' : 'items-start'
                  }`}>
                    <div className={`rounded-2xl px-4 py-2 ${
                      message.sender === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-900 border border-gray-200'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {message.content || message.message}
                      </p>

                      {/* Audio Player */}
                      {message.hasAudio && message.audioId && (
                        <div className="mt-2 pt-2 border-t border-opacity-20 border-gray-300">
                          <AudioPlayer audioId={message.audioId} role={message.role || message.sender} />
                        </div>
                      )}
                    </div>

                    {/* Timestamp */}
                    <span className="text-xs text-gray-500 mt-1 px-2">
                      {formatTimestamp(message.created_at || message.timestamp)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with feedback if available */}
        {session.feedback && (
          <div className="p-6 border-t border-gray-200 bg-amber-50 flex-shrink-0">
            <h3 className="font-semibold text-gray-900 mb-2">Обратная связь AI:</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{session.feedback}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionTranscript;
